import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  DocumentAnnotation,
  DocumentAnnotations,
  HighlightedEntity,
  RemoveAnnotationPayload,
  RemoveAnnotationsForEntitiesPayload,
  SetIsLoadingAnnotationsPayload,
  ShiftAnnotationEntityPayload,
  ShiftAnnotationPayload,
  UpdateAnnotation,
  UpdateAnnotationNormalization,
  UpdateMultiGroupBlockValuePayload,
} from "../interfaces/annotation";
import { Annotation } from "../../annotator/interfaces/annotation";
import { Entity } from "../../annotator/interfaces/entity";
import { baseScale } from "../constants";
import {
  flattenGroupedOutput,
  getAnnotationsInReducer,
  getSideAndTableControlAnnotations,
  groupAnnotations,
} from "../utils/utils";
import { RootState } from "../../app";
import { incrementOperations } from "../../analytics/store/analyticsSlice";

export interface AnnotationState {
  isDragging: boolean;
  scale: number;
  sideControlAnnotations: Array<Annotation>;
  tableControlAnnotations: Array<Annotation>;
  highlightedEntities: Array<HighlightedEntity>;
  lastAnnotation: Annotation | null;
  // Annotation ids showing a loading spinner; kept off the annotation objects so
  // the on-init document rebuilds can't reset it.
  loadingAnnotationIds: Array<string>;
}

const initialState: AnnotationState = {
  isDragging: false,
  scale: baseScale,
  sideControlAnnotations: [],
  tableControlAnnotations: [],
  highlightedEntities: [],
  lastAnnotation: null,
  loadingAnnotationIds: [],
};

export const shiftAnnotationEntitiesWithAnalytics = createAsyncThunk(
  "annotation/shiftAnnotationEntitiesWithAnalytics",
  async (payload: ShiftAnnotationEntityPayload, { dispatch, getState }) => {
    dispatch(shiftAnnotationEntities(payload));
    dispatch(
      incrementOperations({
        numberOfAdditions: 0,
        numberOfDeletions: 0,
        numberOfUpdates: 1,
      }),
    );

    const updatedState = getState() as RootState;
    const updatedAnnotations =
      updatedState.annotationReducer.tableControlAnnotations;

    return updatedAnnotations.filter(
      (a) => a.entity.id === payload.targetEntity.id && a.triggerNormalization,
    );
  },
);

export const annotationSlice = createSlice({
  name: "annotation",
  initialState,
  reducers: {
    addAnnotationsForDocument: (
      state,
      action: PayloadAction<DocumentAnnotations>,
    ) => {
      const { annotations } = action.payload;

      const { sideControlAnnotations, tableControlAnnotations } =
        getSideAndTableControlAnnotations(annotations);

      state.sideControlAnnotations = sideControlAnnotations;
      state.tableControlAnnotations = tableControlAnnotations;
    },
    addAnnotationToState: (
      state,
      action: PayloadAction<DocumentAnnotation>,
    ) => {
      const { annotation, multipleGroupBlocks, documentId } = action.payload;
      const annotations = getAnnotationsInReducer(state, multipleGroupBlocks);

      const newAnnotations = [
        ...annotations,
        {
          ...annotation,
          documentId,
          modelScore: annotation.modelScore || 100,
        },
      ];
      const grouped = groupAnnotations(newAnnotations);
      const convertedAnnotations = flattenGroupedOutput(grouped);

      state.lastAnnotation = annotation;
      if (multipleGroupBlocks) {
        state.tableControlAnnotations = convertedAnnotations;
      } else {
        state.sideControlAnnotations = convertedAnnotations;
      }
    },
    updateAnnotationInState: (
      state,
      action: PayloadAction<UpdateAnnotation>,
    ) => {
      const {
        annotation: annotationToUpdate,
        method,
        newValue,
        options,
        errorMessage,
        multipleGroupBlocks,
        isByUser,
        errorType,
      } = action.payload;

      const annotations = getAnnotationsInReducer(state, multipleGroupBlocks);

      const convertedAnnotations = [...annotations].map((annotation) => {
        if (annotation.id === annotationToUpdate.id) {
          annotation.entityAnnotationNormalization = {
            ...annotation.entityAnnotationNormalization,
            normalizedMethod: method,
            normalizedValue: newValue,
            options,
            errorMessage,
            isByUser,
            errorType,
          };
        }
        return annotation;
      });

      state.lastAnnotation = annotationToUpdate;

      if (multipleGroupBlocks) {
        state.tableControlAnnotations = convertedAnnotations;
      } else {
        state.sideControlAnnotations = convertedAnnotations;
      }
    },
    updateAnnotationNormalizationInState: (
      state,
      action: PayloadAction<UpdateAnnotationNormalization>,
    ) => {
      const {
        id: idToUpdate,
        method,
        newValue,
        options,
        errorMessage,
        multipleGroupBlocks,
        isByUser,
        errorType,
      } = action.payload;

      const annotations = getAnnotationsInReducer(state, multipleGroupBlocks);

      const convertedAnnotations = [...annotations].map((annotation) => {
        if (annotation.id === idToUpdate) {
          annotation.entityAnnotationNormalization = {
            ...annotation.entityAnnotationNormalization,
            normalizedMethod: method,
            normalizedValue: newValue,
            options,
            errorMessage,
            isByUser,
            errorType,
          };
        }
        return annotation;
      });

      if (multipleGroupBlocks) {
        state.tableControlAnnotations = convertedAnnotations;
      } else {
        state.sideControlAnnotations = convertedAnnotations;
      }
    },
    updateAnnotationsBatch: (
      state,
      action: PayloadAction<DocumentAnnotations>,
    ) => {
      const { annotations, documentId, multipleGroupBlocks } = action.payload;

      const annotationsInReducer = getAnnotationsInReducer(
        state,
        multipleGroupBlocks!,
      );

      if (!annotationsInReducer) {
        if (multipleGroupBlocks) {
          state.tableControlAnnotations = annotations;
        } else {
          state.sideControlAnnotations = annotations;
        }
        return;
      }

      const updatedIds = (annotations || []).map((annotation) => annotation.id);

      const newAnnotations = [
        ...annotationsInReducer
          .filter((annotation) => !updatedIds.includes(annotation.id))
          .concat(annotations)
          .map((annotation) => ({
            ...annotation,
            documentId: documentId,
            index: annotation.index,
            modelScore: annotation.modelScore || 100,
            isByUser: annotation.isByUser,
            triggerNormalization: false,
          }))
          .filter(
            (annotation) =>
              !annotation.id || updatedIds.includes(annotation.id),
          ),
      ];

      const grouped = groupAnnotations(newAnnotations);

      const convertedAnnotations = [
        ...annotationsInReducer.filter((ea) => ea.documentId !== documentId),
        ...flattenGroupedOutput(grouped),
      ].filter(
        (annotation, index, self) =>
          index === self.findIndex((a) => a.id === annotation.id),
      );

      state.lastAnnotation =
        convertedAnnotations[convertedAnnotations.length - 1];

      if (multipleGroupBlocks) {
        state.tableControlAnnotations = convertedAnnotations;
      } else {
        state.sideControlAnnotations = convertedAnnotations;
      }
    },
    removeAnnotationFromState: (
      state,
      action: PayloadAction<RemoveAnnotationPayload>,
    ) => {
      const annotations = getAnnotationsInReducer(
        state,
        action.payload.multipleGroupBlocks,
      );

      const annotationToRemove = annotations.find(
        (a) => a.id === action.payload.annotationId,
      );

      if (!annotationToRemove) {
        return;
      }

      let convertedAnnotations = [...annotations].filter(
        (annotation) => annotation.id !== action.payload.annotationId,
      );

      // Make sure that the next annotation is marked as output if applicable
      if (annotationToRemove.isOutput) {
        let newOutputHasBeenSet = false;

        convertedAnnotations = [...convertedAnnotations].map((a) => {
          if (
            a.entity.id === annotationToRemove.entity.id &&
            a.index === annotationToRemove.index &&
            !newOutputHasBeenSet
          ) {
            newOutputHasBeenSet = true;
            return {
              ...a,
              isOutput: true,
            };
          }

          return a;
        });
      }

      if (action.payload.multipleGroupBlocks) {
        state.tableControlAnnotations = convertedAnnotations;
      } else {
        state.sideControlAnnotations = convertedAnnotations;
      }
    },
    removeAnnotationsForEntitiesByGroupBlockIndex: (
      state,
      action: PayloadAction<RemoveAnnotationsForEntitiesPayload>,
    ) => {
      const {
        groupBlockIndex,
        entityIds,
        isGroupRemoved,
        multipleGroupBlocks,
      } = action.payload;

      const annotations = getAnnotationsInReducer(state, multipleGroupBlocks);

      const convertedAnnotations = [...annotations]
        .filter(
          (annotation) =>
            !(
              entityIds.includes(annotation.entity.id) &&
              annotation.index === groupBlockIndex
            ),
        )
        .map((annotation) => {
          if (!isGroupRemoved) {
            return annotation;
          }

          if (
            entityIds.includes(annotation.entity.id) &&
            annotation.index! > groupBlockIndex
          ) {
            return {
              ...annotation,
              index: annotation.index! - 1,
            };
          }
          return annotation;
        });

      if (multipleGroupBlocks) {
        state.tableControlAnnotations = convertedAnnotations;
      } else {
        state.sideControlAnnotations = convertedAnnotations;
      }
    },
    removeAllAnnotationsForDocument: (state, action: PayloadAction<string>) => {
      state.sideControlAnnotations = [
        ...state.sideControlAnnotations.filter(
          (ea) => ea.documentId !== action.payload,
        ),
      ];
      state.tableControlAnnotations = [
        ...state.tableControlAnnotations.filter(
          (ea) => ea.documentId !== action.payload,
        ),
      ];
    },
    changeScale: (state, action: PayloadAction<number>) => {
      state.scale = action.payload;
    },
    resetAnnotationReducer: () => initialState,
    updateGroupBlockAnnotations: (
      state,
      action: PayloadAction<UpdateMultiGroupBlockValuePayload>,
    ) => {
      const { groupBlockIndex, entityIds, addBefore, multipleGroupBlocks } =
        action.payload;

      const annotations = getAnnotationsInReducer(
        state,
        action.payload.multipleGroupBlocks,
      );

      const convertedAnnotations = [...annotations].map((annotation) => {
        if (!annotation.index) {
          return annotation;
        }
        if (
          addBefore &&
          annotation.index - 1 === 0 &&
          entityIds.includes(annotation.entity.id)
        ) {
          return { ...annotation, index: annotation.index + 1 };
        }
        if (
          annotation.index - 1 > groupBlockIndex &&
          entityIds.includes(annotation.entity.id)
        ) {
          return { ...annotation, index: annotation.index + 1 };
        }
        return annotation;
      });

      if (multipleGroupBlocks) {
        state.tableControlAnnotations = convertedAnnotations;
      } else {
        state.sideControlAnnotations = convertedAnnotations;
      }
    },
    removeAnnotationsForEntity: (
      state,
      action: PayloadAction<RemoveAnnotationsForEntitiesPayload>,
    ) => {
      const { entityIds, multipleGroupBlocks } = action.payload;

      const annotations = getAnnotationsInReducer(
        state,
        action.payload.multipleGroupBlocks,
      );

      const convertedAnnotations = [...annotations].filter(
        (annotation) => !entityIds.includes(annotation.entity.id),
      );

      if (multipleGroupBlocks) {
        state.tableControlAnnotations = convertedAnnotations;
      } else {
        state.sideControlAnnotations = convertedAnnotations;
      }
    },
    setLoadingAnnotations: (
      state,
      action: PayloadAction<SetIsLoadingAnnotationsPayload>,
    ) => {
      const { ids, isLoading } = action.payload;
      if (isLoading) {
        state.loadingAnnotationIds = Array.from(
          new Set([...state.loadingAnnotationIds, ...ids]),
        );
      } else {
        state.loadingAnnotationIds = state.loadingAnnotationIds.filter(
          (id) => !ids.includes(id),
        );
      }
    },
    setIsDragging: (state, action: PayloadAction<boolean>) => {
      state.isDragging = action.payload;
    },
    setHighlightedEntities: (
      state,
      action: PayloadAction<Array<HighlightedEntity>>,
    ) => {
      state.highlightedEntities = action.payload;
    },
    setAnnotations: (
      state,
      action: PayloadAction<{
        sideControlAnnotations: Array<Annotation>;
        tableControlAnnotations: Array<Annotation>;
      }>,
    ) => {
      state.sideControlAnnotations = action.payload.sideControlAnnotations;
      state.tableControlAnnotations = action.payload.tableControlAnnotations;
    },
    resetLastAnnotation: (state) => {
      state.lastAnnotation = null;
    },
    addOrUpdateAnnotation: (
      state,
      action: PayloadAction<DocumentAnnotation>,
    ) => {
      const { annotation, multipleGroupBlocks } = action.payload;
      const annotations = getAnnotationsInReducer(state, multipleGroupBlocks);

      const existingValuesJoined = (values: Array<string>) =>
        values.join(" ").toLowerCase();
      const newAnnotationValues = existingValuesJoined(annotation.values);

      let isUpdated = false;
      let isExisting = false;

      const updatedAnnotations = annotations.map((existingAnnotation) => {
        if (
          existingAnnotation.entity.id === annotation.entity.id &&
          existingAnnotation.index === annotation.index
        ) {
          isExisting = true;
          const existingAnnotationValues = existingValuesJoined(
            existingAnnotation.values,
          );
          if (existingAnnotationValues !== newAnnotationValues) {
            isUpdated = true;
            return {
              ...existingAnnotation,
              values: annotation.values,
            };
          }
        }
        return existingAnnotation;
      });

      if (!isUpdated && !isExisting) {
        updatedAnnotations.push(annotation);
      }

      if (multipleGroupBlocks) {
        state.tableControlAnnotations = updatedAnnotations;
      } else {
        state.sideControlAnnotations = updatedAnnotations;
      }
    },
    shiftAnnotation: (state, action: PayloadAction<ShiftAnnotationPayload>) => {
      const { entityTypeId, index, direction } = action.payload;
      const annotations = getAnnotationsInReducer(state, true);

      state.tableControlAnnotations = annotations.map((annotation) => {
        if (
          annotation.entity.id === entityTypeId &&
          annotation.index !== undefined &&
          annotation.index >= index
        ) {
          return {
            ...annotation,
            index: annotation.index + direction,
          };
        }

        return annotation;
      });
    },
    shiftAnnotationEntities: (
      state,
      action: PayloadAction<ShiftAnnotationEntityPayload>,
    ) => {
      const { sourceEntityId, targetEntity } = action.payload;
      const annotations = getAnnotationsInReducer(state, true);

      // First pass: find the source entity object from an annotation
      let sourceEntity: Entity | undefined;
      for (const annotation of annotations) {
        if (annotation.entity.id === sourceEntityId) {
          sourceEntity = annotation.entity;
          break;
        }
      }

      state.tableControlAnnotations = annotations.map((annotation) => {
        // Handle annotations with sourceEntityId - always change to targetEntity
        if (annotation.entity.id === sourceEntityId) {
          return {
            ...annotation,
            entity: targetEntity,
            isByUser: true,
            triggerNormalization: !!targetEntity.entityNormalizations?.length,
            entityAnnotationNormalization: targetEntity.entityNormalizations
              ?.length
              ? annotation.entityAnnotationNormalization
              : undefined,
            modelScore: 100,
          };
        }

        // Handle annotations with targetEntityId that need to be swapped to sourceEntity
        if (annotation.entity.id === targetEntity.id && sourceEntity) {
          const hasSourceAtSameIndex = annotations.find(
            (a) =>
              a.entity.id === sourceEntityId && a.index === annotation.index,
          );

          if (hasSourceAtSameIndex) {
            // Swap this annotation to sourceEntity
            return {
              ...annotation,
              entity: sourceEntity,
              isByUser: true,
              triggerNormalization: !!sourceEntity.entityNormalizations?.length,
              entityAnnotationNormalization: sourceEntity.entityNormalizations
                ?.length
                ? annotation.entityAnnotationNormalization
                : undefined,
              modelScore: 100,
            };
          }
        }

        return annotation;
      });
    },
  },
});

export const {
  addAnnotationsForDocument,
  addAnnotationToState,
  updateAnnotationInState,
  updateAnnotationNormalizationInState,
  updateAnnotationsBatch,
  removeAnnotationFromState,
  removeAnnotationsForEntitiesByGroupBlockIndex,
  removeAllAnnotationsForDocument,
  changeScale,
  resetAnnotationReducer,
  updateGroupBlockAnnotations,
  removeAnnotationsForEntity,
  setLoadingAnnotations,
  setIsDragging,
  setHighlightedEntities,
  setAnnotations,
  resetLastAnnotation,
  addOrUpdateAnnotation,
  shiftAnnotation,
  shiftAnnotationEntities,
} = annotationSlice.actions;

export default annotationSlice.reducer;
